// Average a list of unit-length embeddings then re-normalize so the result is
// itself unit-length. This is the canonical "mean direction" of a set of
// directions on the unit hypersphere — close enough for cosine-similarity
// scoring at the small reference counts we expect (a few clicks).

export function meanEmbedding(
  vectors: ReadonlyArray<{ values: ReadonlyArray<number> }>,
): Float32Array {
  if (vectors.length === 0) {
    throw new Error('meanEmbedding: cannot average an empty list');
  }

  const dim = vectors[0].values.length;

  if (dim === 0) {
    throw new Error('meanEmbedding: zero-dimensional vector');
  }

  const sum = new Float32Array(dim);

  for (const v of vectors) {
    if (v.values.length !== dim) {
      throw new Error(
        `meanEmbedding: dim mismatch (${v.values.length} vs ${dim})`,
      );
    }

    for (let i = 0; i < dim; i += 1) {
      sum[i] += v.values[i];
    }
  }

  // Mean — but we re-normalize next, so the divide is redundant for direction.
  // Keep it explicit because if all inputs cancel we want a clean zero before
  // the normalize-or-fallback decision.
  const inv = 1 / vectors.length;

  for (let i = 0; i < dim; i += 1) {
    sum[i] *= inv;
  }

  let norm = 0;

  for (let i = 0; i < dim; i += 1) {
    norm += sum[i] * sum[i];
  }

  norm = Math.sqrt(norm);

  if (norm === 0) {
    // Degenerate: inputs averaged to the zero vector. Returning zeros is
    // safest — downstream cosine becomes 0 for every patch, so nothing
    // exceeds threshold.
    return sum;
  }

  const invNorm = 1 / norm;

  for (let i = 0; i < dim; i += 1) {
    sum[i] *= invNorm;
  }

  return sum;
}
